import { useCallback, useState } from 'react';

import { validateComment } from '@/utils/validation';

export interface InlineCommentFormProps {
  /** File path the comment is on */
  filePath: string;
  /** Line number the comment is anchored to */
  lineNumber: number;
  /** Side of the diff (LEFT for removed lines, RIGHT for added lines) */
  side: 'LEFT' | 'RIGHT';
  /** Called when the user submits the comment */
  onSubmit: (params: {
    filePath: string;
    lineNumber: number;
    side: 'LEFT' | 'RIGHT';
    body: string;
  }) => Promise<void>;
  /** Called when the user cancels */
  onCancel: () => void;
}

/**
 * Inline comment form that appears below a diff line when the gutter is clicked.
 * Validates input with validateComment (max 65536 chars, non-whitespace-only).
 * Retains text on submission error.
 */
export function InlineCommentForm({
  filePath,
  lineNumber,
  side,
  onSubmit,
  onCancel,
}: InlineCommentFormProps) {
  const [body, setBody] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validation = validateComment(body);
  const isDisabled = !validation.valid || isSubmitting;

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!validation.valid) return;

      setIsSubmitting(true);
      setError(null);

      try {
        await onSubmit({ filePath, lineNumber, side, body });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to submit comment';
        setError(message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [body, filePath, lineNumber, side, onSubmit, validation.valid],
  );

  return (
    <form
      onSubmit={handleSubmit}
      className="border-t border-b border-border bg-surface-raised px-4 py-3"
      data-testid="inline-comment-form"
      aria-label={`Add comment on line ${lineNumber}`}
    >
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Write a comment..."
        maxLength={65536}
        rows={3}
        className="w-full resize-y rounded border border-border bg-surface px-3 py-2 text-sm text-content placeholder-content-tertiary focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600"
        aria-label="Comment body"
        disabled={isSubmitting}
      />

      {/* Validation message */}
      {body.length > 0 && !validation.valid && validation.error && (
        <p className="mt-1 text-xs text-danger" role="alert">
          {validation.error}
        </p>
      )}

      {/* Submission error */}
      {error && (
        <p
          className="mt-1 text-xs text-danger"
          role="alert"
          data-testid="submission-error"
        >
          {error}
        </p>
      )}

      <div className="mt-2 flex items-center gap-2">
        <button
          type="submit"
          disabled={isDisabled}
          className="rounded bg-primary px-3 py-1.5 text-xs font-medium text-content-on-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="rounded border border-border bg-surface px-3 py-1.5 text-xs font-medium text-content-secondary hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
